<script setup lang="ts">
import gsap from 'gsap';
import { useHybridChat } from 'src/composables/hybrid-chat/useHybridChat';
import type { ChatChip } from 'src/composables/hybrid-chat/types';
import { useChatUI } from 'src/composables/useChatUI';
import { useVoiceInput } from 'src/composables/useVoiceInput';
import type { VoiceInputErrorKind } from 'src/composables/useVoiceInput';
import AiChatMessage from './AiChatMessage.vue';
import AiChatChips from './AiChatChips.vue';
import AiChatContactInput from './AiChatContactInput.vue';

const { t, te, locale } = useI18n();

const $q = useQuasar();

const { isOpen, toggle } = useChatUI();

const {
  messages,
  isStreaming,
  error,
  stopStreaming,
  currentChips,
  isCollectingContact,
  currentDialogStep,
  handleUserMessage,
  handleChipClick,
  skipContact,
  reset,
} = useHybridChat();

const inputText = ref('');
const voiceInterim = ref('');
const messagesContainerRef = ref<HTMLElement | null>(null);
const fabRef = ref<HTMLButtonElement | null>(null);

let gsapCtx: gsap.Context;

// Есть ли сообщения
const hasMessages = computed(() => messages.value.length > 0);

// Показывать ли чипы (после последнего сообщения ассистента, если не стримим)
const showChips = computed(() => {
  if (isStreaming.value || isCollectingContact.value) return false;
  return currentChips.value.length > 0;
});

// Локализованное сообщение об ошибке
const errorMessage = computed(() => {
  if (!error.value) return '';
  const key = `AI_CHAT.${error.value}`;
  return te(key) ? t(key) : t('AI_CHAT.ERROR');
});

// Авто-скролл к последнему сообщению
watch(
  () => messages.value.map((m) => m.content).join(''),
  () => {
    nextTick(() => {
      if (messagesContainerRef.value) {
        messagesContainerRef.value.scrollTop =
          messagesContainerRef.value.scrollHeight;
      }
    });
  },
);

// no-speech — штатная тишина при записи, не показываем как ошибку
const VOICE_ERROR_MESSAGE_KEY: Record<VoiceInputErrorKind, string | null> = {
  denied: 'AI_CHAT.VOICE_DENIED',
  'no-speech': null,
  network: 'AI_CHAT.VOICE_ERROR',
  'audio-capture': 'AI_CHAT.VOICE_NO_MIC',
  generic: 'AI_CHAT.VOICE_ERROR',
};

const handleVoiceFinal = (text: string) => {
  voiceInterim.value = '';
  inputText.value = inputText.value.trim()
    ? `${inputText.value.replace(/\s+$/, '')} ${text}`
    : text;
};

const handleVoiceError = (kind: VoiceInputErrorKind) => {
  voiceInterim.value = '';
  const key = VOICE_ERROR_MESSAGE_KEY[kind];
  if (key) $q.notify({ type: 'negative', message: t(key) });
};

const {
  isSupported: voiceSupported,
  isListening: voiceListening,
  toggle: toggleVoice,
  stop: stopVoice,
} = useVoiceInput({
  language: () => (locale.value === 'ru' ? 'ru-RU' : 'en-US'),
  onFinal: handleVoiceFinal,
  onInterim: (text) => (voiceInterim.value = text),
  onError: handleVoiceError,
});

const toggleChat = () => {
  if (isOpen.value) stopVoice();
  toggle();
};

const handleSend = () => {
  if (!inputText.value.trim() || isStreaming.value) return;
  stopVoice();
  handleUserMessage(inputText.value);
  inputText.value = '';
};

const handleChipSelect = (chip: ChatChip) => {
  handleChipClick(chip);
};

const handleContactSubmit = (value: string) => {
  handleUserMessage(value);
};

const handleContactSkip = () => {
  skipContact();
};

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    handleSend();
  }
};

const onPanelKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    toggleChat();
    nextTick(() => fabRef.value?.focus());
  }
};

onMounted(() => {
  gsapCtx = gsap.context(() => {});
});

onUnmounted(() => {
  stopStreaming();
  gsapCtx?.revert();
});
</script>

<template lang="pug">
.ai-chat
  //- Плавающая кнопка
  q-btn.ai-chat__fab(
    ref="fabRef"
    type="button"
    round
    unelevated
    color="dark"
    :ripple="false"
    @click="toggleChat"
    :aria-label="isOpen ? t('AI_CHAT.CLOSE') : t('AI_CHAT.OPEN')"
    :aria-expanded="isOpen"
    aria-controls="ai-chat-panel"
    :class="{ 'ai-chat__fab--active': isOpen }"
  )
    svg.ai-chat__fab-icon(
      v-show="!isOpen"
      width="24" height="24" viewBox="0 0 24 24" fill="none"
      aria-hidden="true"
      focusable="false"
    )
      path(d="M12 2C6.48 2 2 6.04 2 11c0 2.76 1.36 5.22 3.5 6.87V22l3.83-2.11c.86.24 1.75.36 2.67.36 5.52 0 10-4.04 10-9S17.52 2 12 2z" fill="currentColor")
      circle(cx="8" cy="11" r="1" fill="white")
      circle(cx="12" cy="11" r="1" fill="white")
      circle(cx="16" cy="11" r="1" fill="white")

    q-icon(
      v-show="isOpen"
      name="close"
      size="24px"
    )

  //- Панель чата (CSS-transition вместо GSAP: GSAP watch бьёт в null на pre-flush)
  transition(name="ai-chat-panel")
    .ai-chat__panel#ai-chat-panel(
      v-if="isOpen"
      @keydown="onPanelKeydown"
    )
      //- Заголовок
      .ai-chat__header
        .ai-chat__header-title
          span.ai-chat__header-dot
          span {{ t('AI_CHAT.TITLE') }}
        q-btn.ai-chat__header-clear(
          v-if="hasMessages"
          flat
          round
          dense
          size="md"
          icon="delete_sweep"
          :aria-label="t('AI_CHAT.CLEAR')"
          @click="reset"
        )

      //- Область сообщений
      .ai-chat__messages(ref="messagesContainerRef")
        //- Сообщения
        AiChatMessage(
          v-for="(msg, i) in messages"
          :key="i"
          :message="msg"
          :is-streaming="isStreaming && i === messages.length - 1 && msg.role === 'assistant'"
        )

        //- Чипы после последнего сообщения
        AiChatChips(
          v-if="showChips"
          :chips="currentChips"
          :disabled="isStreaming"
          @select="handleChipSelect"
        )

        //- Инлайн-поле ввода контакта
        .ai-chat__contact-input(v-if="isCollectingContact")
          AiChatContactInput(
            :type="currentDialogStep?.collect || 'phone'"
            :disabled="isStreaming"
            @submit="handleContactSubmit"
            @skip="handleContactSkip"
          )

        //- Ошибка
        .ai-chat__error(v-if="errorMessage")
          span {{ errorMessage }}

      //- Input
      .ai-chat__input
        .ai-chat__voice-status(v-if="voiceListening")
          span.ai-chat__voice-dot(aria-hidden="true")
          span.ai-chat__voice-text {{ t('AI_CHAT.VOICE_LISTENING') }}{{ voiceInterim ? ': ' + voiceInterim : '' }}
        .ai-chat__input-row
          textarea.ai-chat__textarea(
            v-model="inputText"
            :placeholder="t('AI_CHAT.PLACEHOLDER')"
            @keydown="handleKeydown"
            rows="1"
            :disabled="isStreaming"
          )
          button.ai-chat__voice-btn(
            v-if="voiceSupported && !isStreaming"
            type="button"
            :class="{ 'ai-chat__voice-btn--active': voiceListening }"
            :aria-label="voiceListening ? t('AI_CHAT.VOICE_STOP') : t('AI_CHAT.VOICE_START')"
            :aria-pressed="voiceListening"
            @click="toggleVoice"
          )
            svg(width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true")
              path(d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" fill="currentColor")
          button.ai-chat__stop-btn(
            v-if="isStreaming"
            @click="stopStreaming"
          ) {{ t('AI_CHAT.STOP') }}
          button.ai-chat__send-btn(
            v-else
            @click="handleSend"
            :disabled="!inputText.trim()"
          )
            svg(width="18" height="18" viewBox="0 0 18 18" fill="none")
              path(d="M2 9l14-7-7 14v-7H2z" fill="currentColor")
</template>

<style lang="sass" scoped>
.ai-chat
  position: fixed
  bottom: 24px
  right: 24px
  z-index: 3000

  &__fab
    width: 56px
    height: 56px
    transition: transform 0.2s

    :deep(.q-btn__content)
      color: white

    &:hover
      transform: scale(1.05)

    &:focus-visible
      outline: 2px solid #fff
      outline-offset: 2px

  &__fab-icon,
  &__fab-close
    transition: opacity 0.2s

  &__panel
    position: absolute
    bottom: 72px
    right: 0
    width: 380px
    max-height: 560px
    background: $dark
    border-radius: 16px
    display: flex
    flex-direction: column
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.4)
    overflow: hidden

  &__header
    display: flex
    align-items: center
    justify-content: space-between
    padding: 16px 20px
    border-bottom: 1px solid rgba(255, 255, 255, 0.08)

    &-title
      display: flex
      align-items: center
      gap: 8px
      font-size: 15px
      font-weight: 600
      color: white

    &-dot
      width: 8px
      height: 8px
      border-radius: 50%
      background: #4ade80

    &-clear
      opacity: 0.4
      transition: opacity 0.2s

      :deep(.q-btn__content)
        color: white

      &:hover
        opacity: 1

      &:focus-visible
        outline: 2px solid #fff
        outline-offset: 2px

  &__messages
    flex: 1
    overflow-y: auto
    padding: 16px
    min-height: 200px
    max-height: 360px

  &__suggestions
    &-title
      font-size: 13px
      color: rgba(255, 255, 255, 0.5)
      margin-bottom: 12px

    &-list
      display: flex
      flex-direction: column
      gap: 8px

  &__suggestion
    background: rgba(255, 255, 255, 0.06)
    border: 1px solid rgba(255, 255, 255, 0.1)
    border-radius: 12px
    padding: 10px 14px
    color: white
    font-size: 13px
    text-align: left
    cursor: pointer
    transition: background 0.2s, border-color 0.2s

    &:hover
      background: rgba(255, 255, 255, 0.1)
      border-color: rgba(255, 255, 255, 0.2)

  &__error
    padding: 8px 12px
    background: rgba(239, 68, 68, 0.1)
    border-radius: 8px
    color: #ef4444
    font-size: 12px
    margin-top: 8px

  &__contact-input
    margin-top: 8px
    padding: 0 4px

  &__input
    padding: 12px 16px
    border-top: 1px solid rgba(255, 255, 255, 0.08)

    &-row
      display: flex
      align-items: flex-end
      gap: 8px

  &__textarea
    flex: 1
    min-width: 0
    background: rgba(255, 255, 255, 0.06)
    border: 1px solid rgba(255, 255, 255, 0.1)
    border-radius: 12px
    padding: 10px 14px
    color: white
    font-size: 14px
    resize: none
    outline: none
    font-family: inherit
    transition: border-color 0.2s

    &::placeholder
      color: rgba(255, 255, 255, 0.3)

    &:focus
      border-color: rgba(255, 255, 255, 0.3)

    &:disabled
      opacity: 0.5

  &__send-btn
    width: 36px
    height: 36px
    border-radius: 50%
    background: white
    color: #1a1a1a
    border: none
    cursor: pointer
    display: flex
    align-items: center
    justify-content: center
    flex-shrink: 0
    transition: opacity 0.2s, transform 0.2s

    &:disabled
      opacity: 0.3
      cursor: not-allowed

    &:not(:disabled):hover
      transform: scale(1.05)

  &__stop-btn
    padding: 6px 14px
    border-radius: 8px
    background: rgba(239, 68, 68, 0.15)
    color: #ef4444
    border: 1px solid rgba(239, 68, 68, 0.3)
    font-size: 12px
    cursor: pointer
    flex-shrink: 0
    transition: background 0.2s

    &:hover
      background: rgba(239, 68, 68, 0.25)

  &__voice-status
    display: flex
    align-items: center
    gap: 8px
    padding: 0 4px 8px
    font-size: 12px
    color: rgba(255, 255, 255, 0.65)

  &__voice-dot
    width: 8px
    height: 8px
    border-radius: 50%
    background: #ef4444
    flex-shrink: 0
    animation: ai-chat-voice-pulse 1.2s ease-in-out infinite

  &__voice-text
    overflow: hidden
    white-space: nowrap
    text-overflow: ellipsis

  &__voice-btn
    width: 36px
    height: 36px
    border-radius: 50%
    background: transparent
    color: rgba(255, 255, 255, 0.6)
    border: 1px solid rgba(255, 255, 255, 0.15)
    cursor: pointer
    display: flex
    align-items: center
    justify-content: center
    flex-shrink: 0
    transition: color 0.2s, border-color 0.2s, transform 0.2s

    &:hover
      color: white
      border-color: rgba(255, 255, 255, 0.35)

    &:focus-visible
      outline: 2px solid #fff
      outline-offset: 2px

    &--active
      color: #ef4444
      border-color: rgba(239, 68, 68, 0.5)
      background: rgba(239, 68, 68, 0.12)

@keyframes ai-chat-voice-pulse
  0%, 100%
    opacity: 1
    transform: scale(1)
  50%
    opacity: 0.4
    transform: scale(0.75)

// Mobile
@media (max-width: 480px)
  .ai-chat
    bottom: 16px
    right: 16px

    &__panel
      position: fixed
      left: 16px
      right: 16px
      bottom: 72px
      width: auto
      max-width: calc(100vw - 32px)
      border-radius: 16px

// Transition
.ai-chat-panel-enter-active
  transition: opacity 0.3s, transform 0.3s

.ai-chat-panel-leave-active
  transition: opacity 0.2s, transform 0.2s

.ai-chat-panel-enter-from,
.ai-chat-panel-leave-to
  opacity: 0
  transform: scale(0.9) translateY(20px)
</style>
